const PAGE_SIZE = 100;
const PAGE_CARD_COUNT = 12;

export const shuffled = (values, random = Math.random) => {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

export const normalizeReview = (raw, context) => {
  const productId = String(raw?.product?.id || '').split('/').pop();
  const rating = Number(raw?.rating);
  if (!raw?.id || !/^\d+$/.test(productId) || !Number.isInteger(rating) || rating < 1 || rating > 5) return null;
  let url;
  try { url = new URL(raw.product.url, context.origin); } catch { return null; }
  if (!['https:', 'http:'].includes(url.protocol) || !context.hosts.includes(url.hostname)) return null;
  const parts = url.pathname.split('/').filter(Boolean);
  const index = parts.lastIndexOf('products');
  if (index < 0 || !parts[index + 1] || parts.length !== index + 2) return null;
  let handle;
  try { handle = decodeURIComponent(parts[index + 1]); } catch { return null; }
  if (!handle || /[/?#]/.test(handle)) return null;
  const media = Array.isArray(raw.media) ? raw.media : [];
  const photo = media.find(m => ['photo', 'image'].includes(m.type) && m.url);
  const video = media.find(m => m.type === 'video');
  const body = typeof raw.body === 'string' && raw.body.trim() ? raw.body : (photo ? 'Photo review.' : (video ? 'Video review.' : ''));
  if (!body) return null;
  return {
    product: { id: productId, handle, title: raw.product.name || 'Personalized gift' },
    review: { id: String(raw.id), productId, rating, body, date: raw.date || raw.createdAt,
      author: raw.reviewer?.name || 'Anonymous', photo: photo?.url || video?.thumbnailUrl || '' },
  };
};

export const loadLivePool = async (apiUrl, context, fetcher = fetch, random = Math.random) => {
  const endpoint = new URL(apiUrl);
  if (endpoint.protocol !== 'https:' || endpoint.hostname !== 'storefront-api.loox.io') throw new Error('Invalid review source');
  const fetchPage = async (page, total) => {
    const url = new URL(endpoint);
    url.searchParams.set('limit', String(PAGE_SIZE));
    url.searchParams.set('sort', 'date');
    url.searchParams.set('direction', 'desc');
    url.searchParams.set('page', String(page));
    if (total !== undefined) url.searchParams.set('total', String(total));
    const response = await fetcher(url.href, { credentials: 'omit', cache: 'no-cache', signal: AbortSignal.timeout(10000) });
    if (!response.ok) {
      const error = new Error('Review source unavailable');
      error.status = response.status;
      throw error;
    }
    const payload = await response.json();
    if (!Array.isArray(payload.reviews) || !Number.isInteger(payload.pagination?.total) || payload.pagination.total < 0) throw new Error('Invalid review response');
    return payload;
  };
  const first = await fetchPage(1);
  const total = first.pagination.total;
  const pageCount = Math.ceil(total / PAGE_SIZE);
  const pages = shuffled(Array.from({ length: Math.max(0, pageCount - 1) }, (_, i) => i + 2), random);
  const products = new Map();
  const reviews = new Map();
  const accept = (payload) => {
    for (const raw of payload.reviews) {
      const item = normalizeReview(raw, context);
      if (item) { products.set(item.product.id, item.product); reviews.set(item.review.id, item.review); }
    }
  };
  accept(first);
  const sampledPages = [1];
  // Always include the newest page so new published reviews can enter the pool,
  // plus random pages so older reviews retain a chance to appear.
  const initialPages = pages.splice(0, 2);
  const extra = await Promise.all(initialPages.map(page => fetchPage(page, total)));
  extra.forEach((payload, i) => { accept(payload); sampledPages.push(initialPages[i]); });
  // Use more random pages only if the first sample lacks product variety.
  while (products.size < 24 && pages.length && sampledPages.length < 6) {
    const page = pages.shift();
    accept(await fetchPage(page, total));
    sampledPages.push(page);
  }
  return { products: [...products.values()], reviews: [...reviews.values()], total, pages: sampledPages };
};

export const fetchPublishedProduct = async (product, routeRoot, fetcher = fetch) => {
  try {
    const response = await fetcher(`${routeRoot}products/${encodeURIComponent(product.handle)}.js`, { credentials: 'same-origin', cache: 'no-cache', signal: AbortSignal.timeout(8000) });
    if (!response.ok) return null;
    const current = await response.json();
    if (String(current.id) !== product.id || current.handle !== product.handle) return null;
    return { ...product, title: current.title || product.title, image: current.featured_image };
  } catch { return null; }
};

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const safeImage = (value) => {
  if (!value) return '';
  try {
    const url = new URL(value, location.origin);
    return url.protocol === 'https:' ? url.href : '';
  } catch { return ''; }
};
const image = (url, className, alt) => {
  const node = element('img', className);
  node.src = url;
  node.alt = alt;
  node.loading = 'lazy';
  node.decoding = 'async';
  node.addEventListener('error', () => { node.hidden = true; }, { once: true });
  return node;
};

const start = async (root) => {
  if (root.dataset.started) return;
  root.dataset.started = 'true';
  root.dataset.source = 'loox-live';
  const grid = root.querySelector('.np-other-reviews__grid');
  const more = root.querySelector('.np-other-reviews__more');
  const reshuffle = root.querySelector('.np-other-reviews__shuffle');
  const status = root.querySelector('.np-other-reviews__status');
  const routeRoot = `${root.dataset.root || '/'}`.replace(/\/?$/, '/');
  const context = { origin: location.origin, hosts: [...new Set([location.hostname, root.dataset.primaryDomain, root.dataset.shopifyDomain].filter(Boolean))] };
  let products = new Map();
  let groups = new Map();
  let queue = [];
  let cursor = 0;
  const cache = new Map();
  const checkProduct = (product) => {
    if (!cache.has(product.id)) cache.set(product.id, fetchPublishedProduct(product, routeRoot));
    return cache.get(product.id);
  };
  const refreshPool = async () => {
    const pool = await loadLivePool(root.dataset.apiUrl, context);
    products = new Map(pool.products.map(p => [p.id, p]));
    groups = new Map();
    for (const review of pool.reviews) {
      if (review.productId === root.dataset.currentProduct) continue;
      if (!groups.has(review.productId)) groups.set(review.productId, []);
      groups.get(review.productId).push(review);
    }
    // Publication status is rechecked after every live refresh. An old 404 must
    // not keep a newly published product out of future batches.
    cache.clear();
    root.dataset.sourceTotal = String(pool.total);
    root.dataset.sampledPages = pool.pages.join(',');
    root.dataset.candidateReviews = String(pool.reviews.length);
    root.dataset.photoReviews = String(pool.reviews.filter(review => review.photo).length);
    root.dataset.sourceSyncedAt = new Date().toISOString();
    root.dataset.sourceError = '';
  };
  const resetQueue = () => {
    queue = shuffled([...groups.keys()]).map(id => {
      const options = groups.get(id);
      return options[Math.floor(Math.random() * options.length)];
    });
    cursor = 0;
  };
  const makeCard = (review, product) => {
    const card = element('a', 'np-other-reviews__card');
    card.href = `${routeRoot}products/${encodeURIComponent(product.handle)}#looxReviews`;
    card.dataset.reviewId = review.id;
    card.dataset.productId = product.id;
    const photo = safeImage(review.photo);
    if (photo) card.append(image(photo, 'np-other-reviews__photo', `Customer photo of ${product.title}`));
    const body = element('div', 'np-other-reviews__body');
    body.append(element('p', 'np-other-reviews__author', review.author));
    const date = new Date(review.date);
    if (!Number.isNaN(date.getTime())) {
      const time = element('time', 'np-other-reviews__date', date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }));
      time.dateTime = date.toISOString();
      body.append(time);
    }
    const stars = element('div', 'np-other-reviews__stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
    stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    const text = element('p', 'np-other-reviews__text');
    const reviewText = review.body.replace(/\r\n?/g, '\n');
    const titledReview = reviewText.match(/^([\s\S]+?)\n[\t ]*\n+([\s\S]+)$/);
    // Imported titles are already separated from the body by a blank line.
    // Keep the original words and emphasise the complete title, including wraps.
    if (titledReview && titledReview[1].trim() && titledReview[2].trim()) {
      text.append(element('strong', 'np-other-reviews__review-title', titledReview[1]), document.createTextNode(`\n\n${titledReview[2]}`));
    } else text.textContent = review.body;
    body.append(stars, text);
    card.append(body);
    const item = element('div', 'np-other-reviews__product');
    const thumb = safeImage(product.image);
    if (thumb) item.append(image(thumb, 'np-other-reviews__thumb', ''));
    item.append(element('span', 'np-other-reviews__product-title', product.title));
    card.append(item, element('span', 'np-other-reviews__link-text', 'View this product →'));
    return card;
  };
  const setBusy = (busy) => { more.disabled = busy; reshuffle.disabled = busy; };
  const failClosed = (error) => {
    // A CSV fallback could reintroduce a review the merchant has unpublished.
    // Show the existing collection link instead of any stale review snapshot.
    grid.replaceChildren();
    grid.hidden = true;
    more.hidden = true;
    reshuffle.hidden = true;
    root.dataset.state = 'unavailable';
    root.dataset.sourceError = String(error?.status || 'unavailable');
    status.textContent = '';
    setBusy(false);
  };
  const addPage = async (replace = false) => {
    const fragment = document.createDocumentFragment();
    let count = 0;
    while (cursor < queue.length && count < PAGE_CARD_COUNT) {
      const batch = queue.slice(cursor, cursor + Math.min(4, PAGE_CARD_COUNT - count));
      cursor += batch.length;
      const checked = await Promise.all(batch.map(r => checkProduct(products.get(r.productId))));
      checked.forEach((product, i) => {
        if (product) { fragment.append(makeCard(batch[i], product)); count++; }
      });
    }
    if (replace) grid.replaceChildren();
    grid.append(fragment);
    if (!grid.children.length) throw new Error('No available reviewed products');
    grid.hidden = false;
    more.hidden = cursor >= queue.length;
    reshuffle.hidden = false;
    root.dataset.state = 'ready';
    status.textContent = `${grid.children.length} reviews of other products`;
  };
  const refresh = async () => {
    setBusy(true);
    status.textContent = 'Loading reviews…';
    try { await refreshPool(); resetQueue(); await addPage(true); }
    catch (error) { failClosed(error); }
    finally { setBusy(false); }
  };
  more.addEventListener('click', async () => {
    setBusy(true);
    status.textContent = 'Loading reviews…';
    try { await addPage(); }
    catch (error) { failClosed(error); }
    finally { setBusy(false); }
  });
  reshuffle.addEventListener('click', refresh);
  await refresh();
};

if (typeof document !== 'undefined') {
  for (const root of document.querySelectorAll('[data-np-other-reviews]')) {
    if (root.dataset.started) continue;
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); start(root); }
      }, { rootMargin: '900px 0px' });
      observer.observe(root);
    } else start(root);
  }
}
